import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { HiPlus, HiTrash, HiX, HiOutlineSearch } from 'react-icons/hi';
import toast from 'react-hot-toast';
import { monthlyReviewsApi, patientsApi, staffApi } from '../../services/api';
import { formatDateDMY } from '../../utils/dateFormat';
import { usePermissions } from '../../hooks/usePermissions';
import { useAuth } from '../../context/AuthContext';
import { useClinic } from '../../context/ClinicContext';
import useDebouncedValue from '../../hooks/useDebouncedValue';

const currentMonthValue = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
};

const formatReviewMonth = (value) => {
  if (!value) return '—';
  const s = String(value).slice(0, 7);
  const [y, m] = s.split('-');
  if (!y || !m) return s;
  const date = new Date(Number(y), Number(m) - 1, 1);
  return date.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
};

const previewNotes = (notes, max = 140) => {
  const text = String(notes || '').trim();
  if (text.length <= max) return text;
  return `${text.slice(0, max)}…`;
};

const MonthlyReviews = () => {
  const { user } = useAuth();
  const { monthlyReviewsTherapistEnabled, setMonthlyReviewsTherapistEnabled } = useClinic();
  const { canCreate, canUpdate, canDelete, isAdmin, isClinicAdmin } = usePermissions();
  const canAdd = canCreate('monthly_reviews');
  const canEdit = canUpdate('monthly_reviews');
  const canRemove = canDelete('monthly_reviews');
  const canFilterByAuthor = isAdmin || isClinicAdmin;
  const currentUserId = Number(user?.id);

  const canEditReview = (review) =>
    canEdit && (canFilterByAuthor || Number(review.created_by) === currentUserId);

  const [filters, setFilters] = useState({
    search: '',
    month: currentMonthValue(),
    patient_id: '',
    date_from: '',
    date_to: '',
    created_by: '',
    page: 1,
  });
  const debouncedSearch = useDebouncedValue(filters.search, 300);
  const [reviews, setReviews] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, totalPages: 1 });
  const [loading, setLoading] = useState(true);
  const [patients, setPatients] = useState([]);
  const [staff, setStaff] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteModal, setDeleteModal] = useState({ open: false, review: null });
  const [alreadyReviewedPatientIds, setAlreadyReviewedPatientIds] = useState(() => new Set());
  const [savingVisibility, setSavingVisibility] = useState(false);
  const [form, setForm] = useState({
    review_month: currentMonthValue(),
    patient_ids: [],
    notes: '',
  });

  const hasActiveFilters = Boolean(
    filters.search ||
      filters.patient_id ||
      filters.date_from ||
      filters.date_to ||
      filters.created_by ||
      (filters.month && filters.month !== currentMonthValue())
  );

  const fetchReviews = useCallback(async () => {
    setLoading(true);
    try {
      const response = await monthlyReviewsApi.getAll({
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
        ...(filters.month ? { month: filters.month } : {}),
        ...(filters.patient_id ? { patient_id: filters.patient_id } : {}),
        ...(filters.date_from ? { date_from: filters.date_from } : {}),
        ...(filters.date_to ? { date_to: filters.date_to } : {}),
        ...(filters.created_by ? { created_by: filters.created_by } : {}),
        page: filters.page,
        limit: 50,
      });
      setReviews(response.data.data?.reviews || []);
      setPagination(response.data.data?.pagination || { total: 0, page: 1, totalPages: 1 });
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to fetch monthly reviews');
      setReviews([]);
      setPagination({ total: 0, page: 1, totalPages: 1 });
    } finally {
      setLoading(false);
    }
  }, [
    debouncedSearch,
    filters.month,
    filters.patient_id,
    filters.date_from,
    filters.date_to,
    filters.created_by,
    filters.page,
  ]);

  const fetchPatients = useCallback(async () => {
    try {
      const response = await patientsApi.getAll({ limit: 200 });
      setPatients(response.data.data?.patients || []);
    } catch (error) {
      setPatients([]);
    }
  }, []);

  const fetchStaff = useCallback(async () => {
    try {
      const response = await staffApi.getAll();
      const list = response.data.data || [];
      setStaff(Array.isArray(list) ? list.filter((person) => person.is_active !== false && person.is_active !== 0) : []);
    } catch (error) {
      setStaff([]);
    }
  }, []);

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  useEffect(() => {
    fetchPatients();
  }, [fetchPatients]);

  useEffect(() => {
    if (canFilterByAuthor) {
      fetchStaff();
    }
  }, [canFilterByAuthor, fetchStaff]);

  const setFilter = (name, value) => {
    setFilters((prev) => ({ ...prev, [name]: value, page: 1 }));
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      month: currentMonthValue(),
      patient_id: '',
      date_from: '',
      date_to: '',
      created_by: '',
      page: 1,
    });
  };

  const emptyListLabel = useMemo(() => {
    if (hasActiveFilters || debouncedSearch || !filters.month) {
      return 'No monthly reviews match these filters.';
    }
    return `No monthly reviews for ${formatReviewMonth(filters.month)}.`;
  }, [hasActiveFilters, debouncedSearch, filters.month]);

  const fetchOwnReviewsForMonth = useCallback(async (monthValue) => {
    if (!monthValue || !currentUserId) {
      setAlreadyReviewedPatientIds(new Set());
      return;
    }
    try {
      const response = await monthlyReviewsApi.getAll({
        month: monthValue,
        created_by: currentUserId,
        limit: 100,
      });
      const ids = new Set(
        (response.data.data?.reviews || []).map((row) => Number(row.patient_id)).filter((id) => id > 0)
      );
      setAlreadyReviewedPatientIds(ids);
    } catch (error) {
      setAlreadyReviewedPatientIds(new Set());
    }
  }, [currentUserId]);

  const openCreateModal = () => {
    const reviewMonth = filters.month || currentMonthValue();
    setEditing(null);
    setForm({
      review_month: reviewMonth,
      patient_ids: [],
      notes: '',
    });
    setShowModal(true);
    fetchOwnReviewsForMonth(reviewMonth);
  };

  const openEditModal = (review) => {
    setEditing(review);
    setForm({
      review_month: String(review.review_month || '').slice(0, 7),
      patient_ids: [review.patient_id],
      notes: review.notes || '',
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditing(null);
    setSaving(false);
    setForm({
      review_month: currentMonthValue(),
      patient_ids: [],
      notes: '',
    });
    setAlreadyReviewedPatientIds(new Set());
  };

  const togglePatient = (patientId) => {
    const id = Number(patientId);
    if (alreadyReviewedPatientIds.has(id)) return;
    setForm((prev) => {
      const exists = prev.patient_ids.includes(id);
      return {
        ...prev,
        patient_ids: exists
          ? prev.patient_ids.filter((x) => x !== id)
          : [...prev.patient_ids, id],
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.review_month) {
      toast.error('Month is required');
      return;
    }
    if (!form.notes.trim()) {
      toast.error('Notes are required');
      return;
    }
    if (!editing && form.patient_ids.length === 0) {
      toast.error('Select at least one patient');
      return;
    }
    if (!editing && form.patient_ids.every((id) => alreadyReviewedPatientIds.has(Number(id)))) {
      toast.error('You already added a review for this patient this month');
      return;
    }

    setSaving(true);
    try {
      if (editing) {
        await monthlyReviewsApi.update(editing.id, {
          notes: form.notes.trim(),
          review_month: form.review_month,
        });
        toast.success('Review updated');
      } else {
        const response = await monthlyReviewsApi.create({
          review_month: form.review_month,
          patient_ids: form.patient_ids,
          notes: form.notes.trim(),
        });
        toast.success(response.data.message || 'Review(s) created');
      }
      closeModal();
      await fetchReviews();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save review');
      setSaving(false);
    }
  };

  const openDeleteModal = (review) => {
    setDeleteModal({ open: true, review });
  };

  const closeDeleteModal = () => {
    setDeleteModal({ open: false, review: null });
  };

  const handleDelete = async () => {
    if (!deleteModal.review) return;
    try {
      await monthlyReviewsApi.delete(deleteModal.review.id);
      toast.success('Review deleted');
      closeDeleteModal();
      await fetchReviews();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete review');
    }
  };

  const handleTherapistVisibilityToggle = async () => {
    const next = !monthlyReviewsTherapistEnabled;
    setSavingVisibility(true);
    try {
      const response = await monthlyReviewsApi.setVisibility({ therapist_enabled: next });
      const enabled = response.data?.data?.therapist_enabled !== false;
      setMonthlyReviewsTherapistEnabled(enabled);
      toast.success(response.data?.message || (enabled ? 'Visible to therapists' : 'Hidden from therapists'));
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update visibility');
    } finally {
      setSavingVisibility(false);
    }
  };

  return (
    <div className="animate-fade-in space-y-4 md:space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h1 className="font-display text-xl font-bold text-gray-900 md:text-2xl">Monthly Reviews</h1>
          <p className="mt-1 text-sm text-gray-500 md:text-base">
            Free-text monthly review notes for patients
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          {canFilterByAuthor ? (
            <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-1.5">
              <div className="min-w-0">
                <p className="text-xs font-medium text-gray-800">Show to therapists</p>
                <p className="text-[10px] text-gray-500">Sidebar tab while this is on</p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={monthlyReviewsTherapistEnabled}
                disabled={savingVisibility}
                onClick={handleTherapistVisibilityToggle}
                className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:opacity-60 ${
                  monthlyReviewsTherapistEnabled ? 'bg-primary-600' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
                    monthlyReviewsTherapistEnabled ? 'translate-x-5' : 'translate-x-0.5'
                  }`}
                />
              </button>
            </div>
          ) : null}
          {canAdd ? (
            <button
              type="button"
              onClick={openCreateModal}
              className="inline-flex min-h-10 shrink-0 items-center justify-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
            >
              <HiPlus className="h-5 w-5" />
              Add Review
            </button>
          ) : null}
        </div>
      </div>

      <div className="card overflow-x-auto p-2">
        <div className="flex min-w-max items-center gap-1.5 lg:min-w-0">
          <div className="relative w-44 shrink-0 lg:w-auto lg:min-w-[10rem] lg:flex-1">
            <HiOutlineSearch className="pointer-events-none absolute left-2 top-1/2 z-10 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={filters.search}
              onChange={(e) => setFilter('search', e.target.value)}
              placeholder="Search name, code, notes"
              className="input h-8 w-full pl-7 text-[11px]"
            />
          </div>

          <select
            value={filters.patient_id}
            onChange={(e) => setFilter('patient_id', e.target.value)}
            className="input h-8 w-40 shrink-0 text-[11px] lg:w-auto lg:min-w-[9rem] lg:flex-1"
            title="Patient"
          >
            <option value="">All patients</option>
            {patients.map((patient) => (
              <option key={patient.id} value={patient.id}>
                {patient.full_name} ({patient.patient_code})
              </option>
            ))}
          </select>

          <input
            type="month"
            value={filters.month}
            onChange={(e) => setFilter('month', e.target.value)}
            className="input h-8 w-[8.5rem] shrink-0 text-[11px]"
            title="Review month"
          />

          {canFilterByAuthor ? (
            <select
              value={filters.created_by}
              onChange={(e) => setFilter('created_by', e.target.value)}
              className="input h-8 w-36 shrink-0 text-[11px] lg:w-auto lg:min-w-[8rem]"
              title="Written by"
            >
              <option value="">All staff</option>
              {staff.map((person) => (
                <option key={person.id} value={person.id}>
                  {person.full_name}
                </option>
              ))}
            </select>
          ) : null}

          <input
            type="date"
            value={filters.date_from}
            onChange={(e) => setFilter('date_from', e.target.value)}
            className="input h-8 w-[8.25rem] shrink-0 text-[11px]"
            title="Created from"
          />
          <span className="shrink-0 text-[10px] text-gray-400">–</span>
          <input
            type="date"
            value={filters.date_to}
            onChange={(e) => setFilter('date_to', e.target.value)}
            min={filters.date_from || undefined}
            className="input h-8 w-[8.25rem] shrink-0 text-[11px]"
            title="Created to"
          />

          <button
            type="button"
            onClick={() => setFilter('month', '')}
            className="shrink-0 whitespace-nowrap px-1 text-[11px] font-medium text-gray-600 hover:text-gray-900"
          >
            All months
          </button>
          {(hasActiveFilters || !filters.month) && (
            <button
              type="button"
              onClick={clearFilters}
              className="shrink-0 whitespace-nowrap px-1 text-[11px] font-medium text-primary-600 hover:text-primary-700"
            >
              Reset
            </button>
          )}
          <span className="ml-auto hidden shrink-0 whitespace-nowrap text-[11px] text-gray-400 xl:inline">
            {loading ? 'Loading…' : `${pagination.total || 0}`}
          </span>
        </div>
      </div>

      <div className="card overflow-hidden p-0 md:p-0">
        {loading ? (
          <div className="flex h-28 items-center justify-center md:h-32">
            <div className="h-7 w-7 animate-spin rounded-full border-2 border-primary-500 border-t-transparent md:h-8 md:w-8" />
          </div>
        ) : reviews.length === 0 ? (
          <p className="px-4 py-8 text-center text-sm text-gray-500 md:py-10">
            {emptyListLabel}
          </p>
        ) : (
          <>
            <div className="divide-y divide-gray-100 lg:hidden">
              {reviews.map((review) => (
                <div key={review.id} className="px-3 py-3 sm:px-4">
                  <div className="mb-1 flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="truncate font-semibold text-gray-900">{review.patient_name}</p>
                      <p className="truncate text-xs text-primary-600">{review.patient_code}</p>
                    </div>
                    <span className="shrink-0 text-xs text-gray-500">{formatReviewMonth(review.review_month)}</span>
                  </div>
                  <p className="mt-2 whitespace-pre-wrap text-sm text-gray-800">{previewNotes(review.notes, 220)}</p>
                  <p className="mt-2 text-[11px] text-gray-500">
                    By {review.created_by_name} · {formatDateDMY(review.created_at)}
                  </p>
                  {(canEditReview(review) || canRemove) && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {canEditReview(review) ? (
                        <button
                          type="button"
                          onClick={() => openEditModal(review)}
                          className="rounded-lg bg-blue-600 px-3 py-2 text-xs font-medium text-white hover:bg-blue-700"
                        >
                          Edit
                        </button>
                      ) : null}
                      {canRemove ? (
                        <button
                          type="button"
                          onClick={() => openDeleteModal(review)}
                          className="inline-flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50"
                        >
                          <HiTrash className="h-3.5 w-3.5" />
                          Delete
                        </button>
                      ) : null}
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="hidden overflow-x-auto lg:block">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 text-left text-gray-500">
                    <th className="px-3 py-2 font-semibold">Patient</th>
                    <th className="px-3 py-2 font-semibold">Month</th>
                    <th className="px-3 py-2 font-semibold">Notes</th>
                    <th className="px-3 py-2 font-semibold">Created By</th>
                    <th className="px-3 py-2 font-semibold">Date</th>
                    {(canEdit || canRemove) && (
                      <th className="px-3 py-2 text-right font-semibold">Actions</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {reviews.map((review) => (
                    <tr key={review.id} className="border-b border-gray-100">
                      <td className="px-3 py-2">
                        <div className="font-medium text-gray-900">{review.patient_name}</div>
                        <div className="text-xs text-gray-500">{review.patient_code}</div>
                      </td>
                      <td className="px-3 py-2 text-gray-700">{formatReviewMonth(review.review_month)}</td>
                      <td className="max-w-md whitespace-pre-wrap px-3 py-2 text-gray-700">
                        {previewNotes(review.notes, 180)}
                      </td>
                      <td className="px-3 py-2 text-gray-700">{review.created_by_name}</td>
                      <td className="px-3 py-2 text-xs text-gray-500">{formatDateDMY(review.created_at)}</td>
                      {(canEdit || canRemove) && (
                        <td className="px-3 py-2">
                          <div className="flex items-center justify-end gap-2">
                            {canEditReview(review) ? (
                              <button
                                type="button"
                                onClick={() => openEditModal(review)}
                                className="rounded-lg bg-blue-600 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-blue-700"
                              >
                                Edit
                              </button>
                            ) : null}
                            {canRemove ? (
                              <button
                                type="button"
                                onClick={() => openDeleteModal(review)}
                                className="inline-flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                              >
                                <HiTrash className="h-3.5 w-3.5" />
                                Delete
                              </button>
                            ) : null}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {pagination.totalPages > 1 ? (
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            disabled={filters.page <= 1}
            onClick={() => setFilters((prev) => ({ ...prev, page: prev.page - 1 }))}
            className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-40"
          >
            Previous
          </button>
          <span className="text-sm text-gray-500">
            Page {pagination.page} of {pagination.totalPages}
          </span>
          <button
            type="button"
            disabled={filters.page >= pagination.totalPages}
            onClick={() => setFilters((prev) => ({ ...prev, page: prev.page + 1 }))}
            className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-40"
          >
            Next
          </button>
        </div>
      ) : null}

      {showModal && (editing ? canEdit : canAdd) && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 sm:items-center sm:p-4">
          <div className="max-h-[92vh] w-full overflow-y-auto rounded-t-2xl bg-white shadow-xl sm:max-h-[90vh] sm:rounded-xl sm:max-w-2xl">
            <div className="flex items-center justify-between border-b border-gray-200 p-4 sm:p-5">
              <h3 className="text-lg font-semibold text-gray-900">
                {editing ? 'Edit Monthly Review' : 'Add Monthly Review'}
              </h3>
              <button type="button" onClick={closeModal} className="text-gray-400 hover:text-gray-600">
                <HiX className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4 p-4 sm:p-5">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Month</label>
                <input
                  type="month"
                  value={form.review_month}
                  onChange={(e) => {
                    const next = e.target.value;
                    setForm((f) => ({
                      ...f,
                      review_month: next,
                      patient_ids: editing ? f.patient_ids : [],
                    }));
                    if (!editing) fetchOwnReviewsForMonth(next);
                  }}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:border-transparent focus:ring-2 focus:ring-primary-500"
                  required
                />
              </div>

              {editing ? (
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Patient</label>
                  <p className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-800">
                    {editing.patient_name} ({editing.patient_code})
                  </p>
                </div>
              ) : (
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    Patients ({form.patient_ids.length} selected)
                  </label>
                  <div className="max-h-48 space-y-1 overflow-y-auto rounded-lg border border-gray-300 p-2">
                    {patients.filter((p) => !p.status || p.status === 'active').length === 0 ? (
                      <p className="px-2 py-3 text-sm text-gray-500">No patients available</p>
                    ) : (
                      patients
                        .filter((p) => !p.status || p.status === 'active')
                        .map((patient) => {
                        const patientId = Number(patient.id);
                        const checked = form.patient_ids.includes(patientId);
                        const alreadyReviewed = alreadyReviewedPatientIds.has(patientId);
                        return (
                          <label
                            key={patient.id}
                            className={`flex items-center gap-2 rounded-md px-2 py-1.5 ${
                              alreadyReviewed ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:bg-gray-50'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              disabled={alreadyReviewed}
                              onChange={() => togglePatient(patient.id)}
                              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                            />
                            <span className="text-sm text-gray-800">
                              {patient.full_name}{' '}
                              <span className="text-gray-500">({patient.patient_code})</span>
                              {alreadyReviewed ? (
                                <span className="ml-1 text-xs text-amber-700">Already reviewed this month</span>
                              ) : null}
                            </span>
                          </label>
                        );
                      })
                    )}
                  </div>
                </div>
              )}

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Notes</label>
                <textarea
                  rows={6}
                  value={form.notes}
                  onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                  className="w-full resize-none rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:border-transparent focus:ring-2 focus:ring-primary-500"
                  placeholder="Enter monthly review notes..."
                  required
                />
              </div>

              <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:justify-end sm:gap-3">
                <button
                  type="button"
                  onClick={closeModal}
                  className="rounded-lg border border-gray-300 px-4 py-2.5 text-gray-700 hover:bg-gray-50 sm:py-2"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="rounded-lg bg-primary-600 px-4 py-2.5 text-white hover:bg-primary-700 disabled:opacity-60 sm:py-2"
                >
                  {saving ? 'Saving...' : editing ? 'Update Review' : 'Save Review'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteModal.open && canRemove && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 sm:items-center sm:p-4">
          <div className="w-full max-w-md rounded-t-2xl bg-white shadow-xl sm:rounded-xl">
            <div className="border-b border-gray-200 p-4 sm:p-5">
              <h3 className="text-lg font-semibold text-gray-900">Delete Monthly Review</h3>
              <p className="mt-1 text-sm text-gray-500">
                Delete the review for{' '}
                <span className="font-medium">{deleteModal.review?.patient_name}</span> (
                {formatReviewMonth(deleteModal.review?.review_month)})?
              </p>
            </div>
            <div className="flex flex-col-reverse gap-2 p-4 sm:flex-row sm:justify-end sm:gap-3 sm:p-5">
              <button
                type="button"
                onClick={closeDeleteModal}
                className="rounded-lg border border-gray-300 px-4 py-2.5 text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDelete}
                className="rounded-lg bg-red-600 px-4 py-2.5 text-white hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MonthlyReviews;
